import React, { useEffect, useRef, useImperativeHandle } from "react";

interface Props {
  width?: number;
  height?: number;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

const SignaturePad: React.FC<Props> = ({ width = 500, height = 300, canvasRef }) => {
  const internalRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const scale = window.devicePixelRatio || 0.5;

  useImperativeHandle(canvasRef, () => internalRef.current!);

  useEffect(() => {
    const canvas = internalRef.current;
    if (!canvas) return;

    const ctx = canvas?.getContext("2d");
    if (ctx) {
      // Set actual canvas size in device pixels
      canvas.width = width * scale;
      canvas.height = height * scale;

      // Set canvas display size (CSS pixels)
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Scale drawing context
      ctx.scale(scale, scale);

      // Init styles
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#000";
    }
  }, [width, height, scale]);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = internalRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    isDrawing.current = true;
  };

  const getCanvasContext = () => {
    const canvas = internalRef.current;
    return canvas ? canvas.getContext("2d") : null;
  };

  const getRelativeCoords = (e: React.MouseEvent) => {
    const canvas = internalRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current) return;
    const ctx = getCanvasContext();
    if (!ctx) return;

    const { x, y } = getRelativeCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  return (
    <canvas
      ref={internalRef}
      width={width}
      height={height}
      style={{ border: "1px solid black", cursor: "crosshair" }}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
    />
  );
};

export default SignaturePad;
